import cn from 'clsx'
import React, {
  forwardRef,
  ButtonHTMLAttributes,
  JSXElementConstructor,
  useRef,
} from 'react'
import { mergeRefs } from 'react-merge-refs'
import { LoadingDots } from '@components/ui'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  href?: string
  target?: string
  rel?: string
  className?: string
  overrideClass?: boolean
  variant?:
    | 'black'
    | 'white'
    | 'outline-black'
    | 'outline-white'
    | 'cta-black'
    | 'cta-white'
  size?: 'sm' | 'md' | 'lg'
  active?: boolean
  type?: 'submit' | 'reset' | 'button'
  Component?: string | JSXElementConstructor<any>
  width?: string | number
  loading?: boolean
  disabled?: boolean
}

const ns = 'button'

// eslint-disable-next-line react/display-name
const Button: React.FC<ButtonProps> = forwardRef((props, buttonRef) => {
  const {
    className,
    overrideClass,
    href,
    target,
    rel,
    variant,
    size,
    children,
    active,
    width,
    loading = false,
    disabled = false,
    style = {},
    Component = 'button',
    ...rest
  } = props
  const ref = useRef<typeof Component>(null)

  const isCTA = variant === 'cta-black' || variant === 'cta-white'

  const rootClassNames = cn({
    [`${className}`]: className,
    [`${ns}`]: !overrideClass,
    [`${ns}--${variant}`]: variant,
    [`${ns}--${size}`]: size,
    [`${ns}--disabled`]: disabled,
  })

  const Arrow = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 16"
        width="24"
        height="16"
      >
        <path
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="2"
          d="M21.75 8.25H1.25m20.5 0-8-6.5m8 6.5-8 6.5"
        />
      </svg>
    )
  }

  if (Component === 'a' && href) {
    return (
      <a
        href={href}
        ref={mergeRefs([ref, buttonRef])}
        className={rootClassNames}
        target={target}
        rel={rel}
      >
        {children}
        {isCTA && <Arrow />}
      </a>
    )
  }

  return (
    <Component
      className={rootClassNames}
      aria-pressed={active}
      data-variant={variant}
      ref={mergeRefs([ref, buttonRef])}
      disabled={disabled}
      {...rest}
      style={style}
    >
      {children}
      {loading && (
        <i className="pl-2 m-0 flex">
          <LoadingDots />
        </i>
      )}
    </Component>
  )
})

export default Button
